'use client';

import {
  handleZipCodeLookup,
  saveLocationAndCheckEligibility,
  type FormState,
} from 'actions/Locations';
import { useAppDispatch, useAppSelector } from 'lib/hooks';
import { useActionState, useCallback, useEffect, useRef } from 'react';
import { Location, selectZipCode, setLocation } from 'slices/locationSlice';
import { clearComparison } from 'slices/comparisonSlice';
import { resetPlans, setMaPlansAvailable } from 'slices/recommendationSlice';
import { ComponentFields } from 'types/content';
import FormError from './FormError';
import { useState } from 'react';
import { useUser } from '@auth0/nextjs-auth0';
import { useRouter } from 'next/navigation';
import CaretIcon from 'components/icons/CaretIcon';
import { ANALYTICS_EVENTS, capture } from 'lib/analytics';
import { writeSessionItem } from 'lib/safeSessionStorage';

const LocationForm = ({ data, onContinue }: ComponentFields) => {
  const router = useRouter();
  const { user: auth0User } = useUser();

  const dispatch = useAppDispatch();
  const userZipCode = useAppSelector(selectZipCode);
  const hasAutoSelectedRef = useRef(false);
  const [showMenu, setShowMenu] = useState(false);
  const [zip, setZip] = useState(userZipCode ?? '');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null
  );

  const [state, formAction, isPending] = useActionState<FormState, FormData>(
    handleZipCodeLookup,
    {
      errors: {},
      locations: [],
    }
  );

  const hasDropdownMenu = state.locations.length > 1;
  const latestSelectionRef = useRef(0);

  const handleLocationSelect = useCallback(
    async (location: Location) => {
      setSelectedLocation(location);

      const selectionId = ++latestSelectionRef.current;

      const onward = () => {
        if (latestSelectionRef.current !== selectionId) return;
        dispatch(clearComparison());
        dispatch(resetPlans());
        dispatch(setLocation(location));
        dispatch(setMaPlansAvailable(true));
        capture(ANALYTICS_EVENTS.locationFormCompleted, {
          zip: location.zipcode,
          state: location.state,
        });
        onContinue();
      };

      if (auth0User) {
        const { eligible } = await saveLocationAndCheckEligibility(location);
        if (latestSelectionRef.current !== selectionId) return;
        if (!eligible) {
          router.push('/nma');
        } else {
          onward();
        }
      } else {
        // If the user is not available (not logged in), skip saveLocation
        if (typeof window !== 'undefined') {
          writeSessionItem('cachedLocation', JSON.stringify(location));
        }
        onward();
      }
    },
    [auth0User, dispatch, onContinue, router]
  );

  useEffect(() => {
    // Reset the auto-select ref when locations change (new search)
    if (state.locations.length !== 1) {
      hasAutoSelectedRef.current = false;
    }

    if (state.locations.length === 1 && !hasAutoSelectedRef.current) {
      hasAutoSelectedRef.current = true;
      const location = state.locations[0];
      Promise.resolve().then(() => handleLocationSelect(location));
    }
  }, [state.locations, handleLocationSelect]);

  const title = data.elements.title.value;
  const countyTitle = data.elements.countyTitle.value;

  return (
    <section className="form-container">
      <form action={formAction}>
        <fieldset>
          <legend>{title}</legend>
          <div className="row">
            <label htmlFor="zip" className="visually-hidden">
              ZIP code
            </label>
            <input
              id="zip"
              type="text"
              name="zip"
              autoComplete="off"
              data-testid="zipcode-input"
              pattern="[0-9]{5}"
              maxLength={5}
              placeholder="Enter a 5-digit ZIP code"
              required
              onChange={(e) => setZip(e.target.value)}
              value={zip}
              className="half"
            />
            {state.errors.zip && <FormError error={state.errors.zip} />}
          </div>
          {isPending && !hasDropdownMenu && <p className="loading-spinner"></p>}
          {state.errors.api && <FormError error={state.errors.api} />}
        </fieldset>

        {state.locations.length > 1 && (
          <fieldset className="county-dropdown">
            <legend>{countyTitle}</legend>
            <div className="form-label">
              <button
                role="combobox"
                id="county-select"
                value="Select"
                aria-controls="listbox"
                aria-haspopup="listbox"
                aria-expanded={showMenu}
                className={`select-button ${showMenu ? 'open' : ''}`}
                type="button"
                onClick={() => setShowMenu(!showMenu)}
              >
                Select County
                <CaretIcon
                  direction={showMenu ? 'up' : 'down'}
                  size="large"
                  color="gray"
                />
              </button>
              <ul
                role="listbox"
                id="listbox"
                className={showMenu ? '' : 'hidden'}
              >
                {state.locations.map((location: Location, idx: number) => {
                  const isSelected =
                    selectedLocation?.countyName === location.countyName;

                  return (
                    <li role="option" key={idx} aria-selected={isSelected}>
                      <button
                        type="button"
                        onClick={() => handleLocationSelect(location)}
                        className="county-option-button"
                      >
                        {location.countyName}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </fieldset>
        )}
        <button
          type="submit"
          className="button primary submit"
          data-testid="submit-zip"
          disabled={isPending}
        >
          Submit
        </button>
      </form>
    </section>
  );
};

export default LocationForm;
